import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type ReadinessState = 'done' | 'progress' | 'blocked' | 'locked';

const ring: Record<ReadinessState, string> = {
  done:     'bg-green-tint text-green-fg ring-green',
  progress: 'bg-amber-tint text-amber-fg ring-amber',
  blocked:  'bg-[oklch(0.96_0.006_250)] text-muted-label ring-[oklch(0.72_0.008_250)]',
  locked:   'bg-indigo-tint text-primary ring-primary',
};

/**
 * Requirement / check row used by TFA readiness and claim readiness checklist.
 * `icon` is a Lucide glyph (Check / CircleDashed / X / Lock).
 */
export function ReadinessRow({
  state,
  icon,
  name,
  detail,
  owner,
  action,
}: {
  state: ReadinessState;
  icon: ReactNode;
  name: string;
  detail?: string;
  owner?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-[15px] rounded-[11px] px-3 py-3.5 hover:bg-surface-3">
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-[1.5px] ring-inset',
          ring[state]
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold text-ink">{name}</span>
        {detail && <span className="block text-[12.5px] text-dim">{detail}</span>}
      </span>
      {owner && <span className="shrink-0 text-[11.5px] text-muted-label">{owner}</span>}
      {action}
    </div>
  );
}

/** Labelled progress bar (effort, velocity, composition). */
export function Bar({
  label,
  value,
  pct,
  tone = 'indigo',
}: {
  label: string;
  value?: string;
  pct: number;
  tone?: 'indigo' | 'green' | 'primary';
}) {
  const fill = { indigo: 'bg-indigo-from', green: 'bg-green', primary: 'bg-primary' }[tone];
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="truncate text-[11.5px] text-ink">{label}</span>
        {value && <span className="font-mono text-[11.5px] font-semibold">{value}</span>}
      </div>
      <div className="h-1.5 overflow-hidden rounded-[var(--radius-pill)] bg-[oklch(0.93_0.006_250)]">
        <div className={cn('h-full rounded-[var(--radius-pill)]', fill)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
